// aifab - apiFetch
//
// Single authenticated fetch function for all API calls.
// On 401: clears all auth storage, fires aifab:logout event.
// On 403: throws with the server message (role/permission error).
// On network failure: throws with a clear message.
//
// apiFetchMultipart is kept for legacy callers but delegates here.

const API = '/api/v1';

function clearAuthStorage() {
  // FE-M2: the token is no longer in localStorage (it's an httpOnly cookie the
  // server clears on logout/401). Only the non-sensitive profile + project hint
  // live here now.
  localStorage.removeItem('aifab_user');
  localStorage.removeItem('aifab_project_id');
}

function fireLogout(reason = 'session_expired') {
  // De-duplicate: if already fired in this tick, skip
  if (window._aifab_logout_fired) return;
  window._aifab_logout_fired = true;
  // Reset flag after current call stack unwinds
  setTimeout(() => { window._aifab_logout_fired = false; }, 100);

  window.dispatchEvent(new CustomEvent('aifab:logout', { detail: { reason } }));
}

export async function apiFetch(path, opts = {}) {
  // FE-M2: auth travels as an httpOnly, Secure, SameSite=Strict cookie set by
  // the server. JS can't read it (XSS can't steal it), so we just send cookies
  // with every request via credentials:'include'. No Authorization header.
  const { raw, ...fetchOpts } = opts;

  const headers = raw || opts.isFormData
    ? {}
    : { 'Content-Type': 'application/json' };

  let res;
  try {
    res = await fetch(API + path, {
      credentials: 'include',
      headers: {
        ...headers,
        ...(fetchOpts.headers || {}),
      },
      ...fetchOpts,
    });
  } catch (networkErr) {
    throw new Error('Network error - check your connection');
  }

  if (res.status === 401) {
    // A 401 from the login attempt itself means the credentials were wrong -
    // there is no session to expire yet. Surface the server's message
    // ("Invalid credentials") instead of the session-expired flow.
    if (path === '/auth/login') {
      const body = await res.json().catch(() => ({}));
      const err  = new Error(body.detail || 'Invalid credentials');
      err.code   = 'INVALID_CREDENTIALS';
      throw err;
    }
    clearAuthStorage();
    fireLogout('session_expired');
    // Throw a sentinel so callers don't need to handle it
    const err = new Error('Session expired');
    err.code = 'SESSION_EXPIRED';
    throw err;
  }

  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.detail || 'Access denied');
    err.code   = 'FORBIDDEN';
    throw err;
  }

  if (!res.ok) {
    const body   = await res.json().catch(() => ({}));
    const detail = body.detail;
    if (Array.isArray(detail)) {
      throw new Error(
        'Validation: ' + detail.map(e => `${e.loc?.slice(1).join('.')} - ${e.msg}`).join('; ')
      );
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }

  return res.status === 204 ? null : res.json();
}

// Legacy alias - same behaviour
export const apiFetchMultipart = (path, formData) =>
  apiFetch(path, { method: 'POST', body: formData, raw: true });
